import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Prompt, useHistory, useParams } from 'react-router';

import { cloneDeep, isEmpty, isEqual } from 'lodash';

import { EvSpinner, useSkipLinks } from '@evinced-private/ui-common';

import { PropertySettingsActions } from '../../components/property-settings/property-settings-actions/PropertySettingsActions';
import { PropertySettingsContent } from '../../components/property-settings/property-settings-content/PropertySettingsContent';
import { PropertySettingsTitle } from '../../components/property-settings/property-settings-title/PropertySettingsTitle';
import { FormError, validateProperty } from '../../helpers/FormHelper';
import { PropertyHelper } from '../../helpers/PropertyHelper';
import { isPropertyInListMode } from '../../helpers/PropertySourceHelper';
import { RoutesHelper } from '../../helpers/RoutesHelper';
import { withLimitedAccess } from '../../hoc/withLimitedAccess';
import { useBlockRefresh } from '../../hooks/useBlockRefresh';
import { IProperty } from '../../interfaces/Property';
import { useNavigation } from '../../providers/navigationProvider/NavigationProvider';
import { useNotifications } from '../../providers/notificationsProvider/NotificationsConsumer';
import {
	changePropertySourceMode,
	createNewProperty,
	createUrlSetAndUpdateProperty,
	getPropertyData,
	updateProperty
} from '../../services/PropertiesService';
import { SETTINGS_SECTION_TYPES } from '../../types/SettingsSectionTypes';
import { URL_SET_SOURCES } from '../../types/UrlSetSources';

import { PropertySettingsSaveErrorHelper } from './helpers/PropertySettingsSaveErrorHelper';

import './PropertySettingsPage.scss';

const PropertySettingsPageInner: FC = () => {
	const [property, setProperty] = useState<IProperty>(null);
	const [originalProperty, setOriginalProperty] = useState<IProperty>(null);
	const [propertyErrors, setPropertyErrors] = useState<FormError[]>([]);
	const [propertySourceMode, setPropertySourceMode] = useState<URL_SET_SOURCES>();
	const [loading, setLoading] = useState(true);
	const [isSaveInProgress, setIsSaveInProgress] = useState(false);

	const notificationsContext = useNotifications();
	const { shouldBlockNavigation, setShouldBlockNavigation } = useBlockRefresh();
	const { propertyId } = useParams<{ [key: string]: string }>();
	const { setCurrentRoute } = useNavigation();
	const history = useHistory();

	const showPropertySaveError = useCallback(
		(err: unknown, title: string): void => {
			notificationsContext.alert?.(
				PropertySettingsSaveErrorHelper.getSaveErrorAlertDetails(title, err)
			);
		},
		[notificationsContext]
	);

	// update skip links list when page is fully loaded
	useSkipLinks(!loading);

	useEffect(() => {
		if (propertyId) {
			setCurrentRoute({
				id: `advanced-settings-${propertyId}`,
				url: RoutesHelper.getPropertySettingsPagePath(propertyId),
				title: 'Advanced settings'
			});
		}
		return () => {
			setCurrentRoute(null);
		};
	}, [propertyId, setCurrentRoute]);

	const isPropertyInListModeCurrently = useMemo((): boolean => {
		return isPropertyInListMode(propertySourceMode);
	}, [propertySourceMode]);

	const removePropertyError = (sectionId: SETTINGS_SECTION_TYPES): void => {
		const newFormErrors = propertyErrors.filter((error) => error.sectionId !== sectionId);
		setPropertyErrors(newFormErrors);
	};

	const setNewPropertyFromRouteParams = useCallback((): void => {
		const queryParams = new URLSearchParams(history.location.search);
		const baseUrl = queryParams.get('baseurl');
		let newProperty: IProperty = PropertyHelper.getDefaultPropertyFromTemplate(baseUrl);
		newProperty = PropertyHelper.ensurePropertyHasDefaultRule(newProperty);
		setProperty(newProperty);
		setPropertySourceMode(URL_SET_SOURCES.URL_DISCOVERY);
		setLoading(false);
	}, [history.location.search]);

	const isPropertyChanged = useMemo((): boolean => {
		return !isEqual(property, originalProperty);
	}, [property, originalProperty]);

	const getCurrentPropertyData = useCallback(
		async (sourceMode?: URL_SET_SOURCES): Promise<void> => {
			setLoading(true);

			getPropertyData(propertyId)
				.then((propertyFromServer) => {
					if (!propertyFromServer) {
						setNewPropertyFromRouteParams();
						return;
					}

					setOriginalProperty(cloneDeep(propertyFromServer));
					setProperty(propertyFromServer);
					const source = sourceMode || propertyFromServer.urlSetConfiguration.source;
					setPropertySourceMode(source);
					setLoading(false);
				})
				.catch((err) => {
					notificationsContext.alert({
						serverError: err,
						onOK: () => history.push(RoutesHelper.getPropertiesPagePath())
					});
				});
		},
		[notificationsContext, history, propertyId, setNewPropertyFromRouteParams]
	);

	const goToProperty = useCallback(
		(propertyId): void => {
			setLoading(true);
			setShouldBlockNavigation(false);
			history.push(RoutesHelper.getPropertyPagePath(propertyId));
		},
		[history, setShouldBlockNavigation]
	);

	const updateCurrentProperty = useCallback(
		(propertyToSave): void => {
			updateProperty(propertyToSave)
				.then(() => {
					goToProperty(property.id);
				})
				.catch((err) => {
					showPropertySaveError(err, 'Cannot save property');
				})
				.finally(() => {
					setIsSaveInProgress(false);
				});
		},
		[goToProperty, property?.id, showPropertySaveError]
	);

	const changeCurrentPropertySourceMode = useCallback(async (): Promise<void> => {
		if (
			isEmpty(property) ||
			!propertySourceMode ||
			property?.urlSetConfiguration.source === propertySourceMode
		) {
			return;
		}
		setLoading(true);
		const newProperty = await changePropertySourceMode(property, propertySourceMode);
		setProperty(newProperty);
		setLoading(false);
	}, [propertySourceMode, setProperty, setLoading, property]);

	// run this when propertySourceMode changes
	useEffect(() => {
		changeCurrentPropertySourceMode();
	}, [changeCurrentPropertySourceMode]);

	// listen to property changes and check if we should block navigation due to them
	useEffect(() => {
		if (property) {
			setShouldBlockNavigation(isPropertyChanged);
		}
	}, [property, isPropertyChanged, setShouldBlockNavigation]);

	const createUrlSetAndCallUpdateProperty = async (propertyToSave: IProperty): Promise<void> => {
		createUrlSetAndUpdateProperty(propertyToSave, isPropertyInListModeCurrently)
			.then((property) => {
				goToProperty(property.id);
			})
			.catch((err) => {
				showPropertySaveError(err, 'Cannot save property');
			})
			.finally(() => {
				setIsSaveInProgress(false);
			});
	};

	const editProperty = (newProperty: IProperty, shouldCrawl: boolean): void => {
		const propertyToSave: IProperty = { ...newProperty };

		if (isPropertyInListModeCurrently || shouldCrawl) {
			createUrlSetAndCallUpdateProperty(propertyToSave);
		} else {
			// update only name
			updateCurrentProperty(propertyToSave);
		}
	};

	const createProperty = async (newProperty: IProperty, shouldCrawl: boolean): Promise<void> => {
		createNewProperty(newProperty, shouldCrawl, isPropertyInListModeCurrently)
			.then((createdProperty) => {
				setIsSaveInProgress(false);
				goToProperty(createdProperty.id);
			})
			.catch((err) => {
				setIsSaveInProgress(false);
				showPropertySaveError(err, 'Cannot create property');
			});
	};

	const onSaveChanges = async (shouldCrawl = false): Promise<void> => {
		const formErrors: FormError[] = validateProperty(property, isPropertyInListModeCurrently);
		setPropertyErrors(formErrors);
		if (formErrors.length > 0) {
			return;
		}

		setIsSaveInProgress(true);

		if (PropertyHelper.isPropertyInDraftMode(propertyId)) {
			createProperty(property, shouldCrawl);
		} else {
			editProperty(property, shouldCrawl);
			setOriginalProperty(property);
		}
	};

	// get property when page loads
	useEffect(() => {
		getCurrentPropertyData();
	}, [propertyId, getCurrentPropertyData]);

	const nameError = useMemo((): FormError => {
		return propertyErrors.find((error) => error.sectionId === SETTINGS_SECTION_TYPES.NAME);
	}, [propertyErrors]);

	if (loading) {
		return (
			<div className="spinner">
				<EvSpinner />
			</div>
		);
	}

	return (
		<div className="property-settings-page">
			<Prompt
				when={shouldBlockNavigation}
				message="You have unsaved changes, are you sure you want to leave?"
			/>
			<PropertySettingsTitle
				propertyId={propertyId}
				originalProperty={originalProperty}
				property={property}
				setProperty={setProperty}
				sourceMode={propertySourceMode}
				nameError={nameError}
				editProperty={editProperty}
				getPropertyData={getCurrentPropertyData}
				goToProperty={goToProperty}
				setPropertySourceMode={setPropertySourceMode}
				setLoading={setLoading}
			/>
			<PropertySettingsContent
				originalProperty={originalProperty}
				property={property}
				setProperty={setProperty}
				propertyErrors={propertyErrors}
				removePropertyError={removePropertyError}
				isPropertyInListMode={isPropertyInListModeCurrently}
			/>
			<PropertySettingsActions
				property={property}
				isPropertyInListMode={isPropertyInListModeCurrently}
				isSaveInProgress={isSaveInProgress}
				onSaveChanges={onSaveChanges}
			/>
		</div>
	);
};

export const PropertySettingsPage = withLimitedAccess(PropertySettingsPageInner);
